<template>
  <div
    ref="appHeaderWrap"
    class="c-w-container sticky top-0 z-50 mx-auto w-full max-w-9xl rounded-b-3xl sm:rounded-b-4xl lg:relative lg:top-auto"
  >
    <header
      class="relative z-10 rounded-b-3xl bg-white p-5 text-gray-900 sm:rounded-b-4xl sm:px-8 md:py-8 lg:px-10"
    >
      <div
        class="mx-auto flex w-full max-w-8xl items-center justify-between gap-8 lg:justify-normal"
      >
        <AppLink
          v-bind="{ to: { name: 'HomePage' } }"
          class="relative transition-opacity hover:opacity-80"
        >
          <IntlMsg v-bind="{ k: 'home.home-link' }" class="sr-only" />
          <AppLogo class="hidden h-12 w-52 sm:inline" />
          <AppLogoMobile class="text-gray-900 sm:hidden" />
        </AppLink>

        <div ref="navWrap" class="hidden grow overflow-hidden lg:flex">
          <nav
            :style="{
              width: navListWidth ? navListWidth + 'px' : undefined,
            }"
            class="flex whitespace-nowrap text-lg font-semibold leading-6"
          >
            <ul class="flex items-center">
              <li
                v-for="(item, i) in navItems"
                ref="navList"
                :key="i"
                class="p-2.5"
                :class="{
                  'pointer-events-none opacity-0':
                    typeof minHiddenNavItemIndex === 'number' &&
                    i >= minHiddenNavItemIndex,
                }"
              >
                <AppLink
                  v-if="item.to"
                  v-bind="{
                    to: item.to,
                    exactActiveClass:
                      'underline underline-offset-[10px] decoration-4 decoration-blue-400',
                  }"
                  class="transition-colors hover:text-blue-700"
                  @click="closeDropdownMenu"
                >
                  <IntlMsg v-bind="{ k: item.key }" />
                </AppLink>
              </li>
            </ul>
          </nav>

          <button
            ref="moreBtn"
            class="outline-main relative inline-flex shrink-0 items-center gap-1 rounded pl-2.5 text-lg font-semibold leading-6 transition-colors hover:text-blue-700"
            @click="toggleDropdownMenu"
          >
            <IntlMsg v-bind="{ k: 'common.more' }" />
            <ChevronDownIcon
              class="h-5 w-5 shrink-0 stroke-2 transition-transform"
              :class="{ 'rotate-180': isOpenedDropdownNav }"
            />
          </button>
        </div>

        <div class="flex items-center gap-6">
          <LocaleSelect
            v-bind="{ hideLabel: true }"
            class="hidden pt-1 text-lg leading-6 lg:inline-flex"
          />

          <div class="flex items-center gap-4 md:gap-6">
            <UserProfileWrap />

            <button
              class="c-btn relative rounded p-0 text-gray-500 active:opacity-60 lg:hidden"
              @click="bus.emit('app::show-mobile-menu')"
            >
              <IntlMsg
                v-bind="{ k: 'common.open-menu-burger' }"
                class="sr-only"
              />
              <Bars3Icon class="h-9 w-9 shrink-0 stroke-2" />
            </button>
          </div>
        </div>
      </div>
    </header>

    <NewCustomersDiscountHeader v-if="hasPromoDiscountHeader" />

    <Transition name="collapse-transform">
      <div
        v-if="isOpenedDropdownNav"
        class="absolute inset-x-0 top-0 z-1 hidden rounded-b-3xl bg-gray-100 pt-[6.625rem] shadow-shade lg:block"
      >
        <div class="container mx-auto py-16">
          <ul class="grid grid-cols-2 gap-16 xl:grid-cols-4 xl:gap-10">
            <li v-for="group in dropdownMenuGroups" :key="group.key">
              <IntlMsg
                v-bind="{ k: group.key }"
                class="mb-5 block border-b-2 border-gray-250 pb-3 text-2xl font-semibold leading-[1.65rem]"
              />
              <ul>
                <li
                  v-for="link in group.children"
                  :key="link.key"
                  class="mb-4.5 text-xl font-semibold leading-dense text-gray-400 last:mb-0"
                >
                  <AppLink
                    v-if="link.to"
                    v-bind="{
                      to: link.to,
                      exactActiveClass: 'text-blue-500',
                    }"
                    class="transition-colors hover:text-blue-500"
                    @click="closeDropdownMenu"
                  >
                    <IntlMsg v-bind="{ k: link.key }" />
                  </AppLink>
                </li>
              </ul>
            </li>
          </ul>
        </div>
      </div>
    </Transition>
  </div>
</template>

<script setup lang="ts">
import { computed, nextTick, ref, watch, useTemplateRef } from 'vue'
import { useRoute } from 'vue-router'
import {
  useDebounceFn,
  useEventListener,
  onClickOutside,
  isClient,
  promiseTimeout,
} from '@vueuse/core'
import { ChevronDownIcon, Bars3Icon } from '@heroicons/vue/24/outline'

import {
  bus,
  useScreens,
  IntlMsg,
  type AppNavGroup,
  type AppNavItem,
} from '/@shared/app'
import {
  AppLogo,
  AppLogoMobile,
  AppLink,
  LocaleSelect,
  UserProfileWrap,
  useLocaleFeatures,
  NewCustomersDiscountHeader,
} from '/@app'

import { useAuthStore } from '/@/stores'

import { useI18n } from '/@/i18n'

const authStore = useAuthStore()

const route = useRoute()

const hasPromoDiscountHeader = computed(() => {
  return (
    route.name === 'VpsPage' ||
    route.name === 'DsPage' ||
    route.name === 'DsPlanPage'
  )
})

const { locale } = useI18n()

const navItems: AppNavItem[] = [
  {
    key: 'header.web-hosting',
    to: { name: 'VhPage' },
  },
  {
    key: 'header.vps',
    to: { name: 'VpsPage' },
  },
  {
    key: 'header.dedicated',
    to: { name: 'DsPage' },
  },
  {
    key: 'header.partner-program',
    to: {
      name: 'HelpArticlePage',
      params: {
        slug: 'partner-program',
      },
    },
  },
  {
    key: 'header.help',
    to: { name: 'HelpIndexPage' },
  },
]

const navWrap = useTemplateRef('navWrap')
const moreBtn = useTemplateRef('moreBtn')
const navItemList = useTemplateRef('navList')

const navListWidth = ref<number>()
const minHiddenNavItemIndex = ref<number | undefined>()

const { isLargeScreen } = useScreens()

function updateNavItemsVisibility() {
  if (!isLargeScreen.value) return
  if (!navWrap.value) return
  if (!navItems?.length) return

  const btnWidth = moreBtn.value?.clientWidth || 0
  const navWidth = navWrap.value.clientWidth - btnWidth

  let itemsWidth = 0
  for (let i = 0; i < navItems.length; i++) {
    const width = navItemList.value?.[i]?.clientWidth
    if (!width) continue

    itemsWidth += width

    if (itemsWidth > navWidth) {
      navListWidth.value = itemsWidth - width
      minHiddenNavItemIndex.value = i
      return
    }
  }

  navListWidth.value = undefined
  minHiddenNavItemIndex.value = undefined
}

watch(locale, () => {
  nextTick(updateNavItemsVisibility)
})
watch(
  () => authStore.user,
  () => {
    nextTick(updateNavItemsVisibility)
  }
)

if (isClient) {
  const debouncedCheckNavVisibility = useDebounceFn(
    updateNavItemsVisibility,
    60
  )
  useEventListener(window, 'resize', debouncedCheckNavVisibility)
  useEventListener(window, 'orientationchange', debouncedCheckNavVisibility)

  watch(
    () => authStore.authStatus,
    async val => {
      if (!isLargeScreen.value) return
      if (!val && authStore.profile !== null) return
      if (val === 'loading') return

      /**
       * await profile transtions and flush
       */
      await promiseTimeout(150)
      updateNavItemsVisibility()
    },
    { immediate: true }
  )
}

const appHeaderWrap = useTemplateRef('appHeaderWrap')

const isOpenedDropdownNav = ref(false)
function closeDropdownMenu() {
  isOpenedDropdownNav.value = false
}
function toggleDropdownMenu() {
  isOpenedDropdownNav.value = !isOpenedDropdownNav.value
}

onClickOutside(appHeaderWrap, () => {
  isOpenedDropdownNav.value = false
})

const { isVpnAvailable } = useLocaleFeatures()
const dropdownMenuGroups = computed(() => {
  const list: AppNavGroup[] = [
    {
      key: 'menu.main',
      children: [
        {
          key: 'menu.home',
          to: { name: 'HomePage' },
        },
        {
          key: 'menu.support',
          to: authStore.supportUrl,
        },
        {
          key: 'menu.news',
          to: { name: 'NewsPage' },
        },
      ],
    },
    {
      key: 'menu.services',
      children: [
        {
          key: 'menu.dedicated',
          to: { name: 'DsPage' },
        },
        {
          key: 'menu.vps',
          to: { name: 'VpsPage' },
        },
        {
          key: 'menu.hosting',
          to: { name: 'VhPage' },
        },
        {
          key: 'menu.backup',
          to: { name: 'BackupPage' },
        },
      ],
    },
  ]

  if (isVpnAvailable.value)
    list[1].children.push({
      key: 'menu.vpn',
      to: { name: 'VpnPage' },
    })

  list.push(
    {
      key: 'menu.company',
      children: [
        {
          key: 'menu.news',
          to: { name: 'NewsPage' },
        },
        {
          key: 'menu.our-services',
          to: { name: 'ServicesPage' },
        },
        {
          key: 'menu.our-customers',
          to: {
            name: 'HomePage',
            hash: '#ourclients',
          },
        },
        {
          key: 'menu.contacts',
          to: { name: 'ContactsPage' },
        },
      ],
    },
    {
      key: 'menu.resources',
      children: [
        {
          key: 'menu.help',
          to: { name: 'HelpIndexPage' },
        },
        {
          key: 'menu.for-partners',
          to: {
            name: 'HelpArticlePage',
            params: {
              slug: 'partner-program',
            },
          },
        },
        {
          key: 'menu.api',
          to: { name: 'ApiPage' },
        },
      ],
    }
  )
  return list
})
</script>
